import React, {useEffect, useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import {useSelector, useDispatch} from "react-redux";
import {ChevronDownIcon, ArrowRightIcon} from '@heroicons/react/24/outline';
import {fetchMarketResources, selectMarketResources} from "../slices/marketSlice";
import useInView from '../hooks/useInView';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import PublicButton from '../components/PublicButton';
import EnsembleDiagram from '../components/EnsembleDiagram';
import inescLogo from '../assets/images/INESCTEC_logotipo_color_rgb.png';

const SLOGANS = [
  'A market where the best energy forecasts win.',
  'Where forecasts compete and performance decides.',
  'Accuracy, measured and rewarded.',
  'Crowdsourced accuracy for the energy grid.',
  'Compete on accuracy. Earn on performance.',
  'Better forecasts. Smarter grids.',
];

const STEPS = [
  {
    n: '01',
    title: 'Submit',
    text: "Send in your time series forecasts before the session's gate closes.",
  },
  {
    n: '02',
    title: 'Blend',
    text: 'At gate closure, every submission merges into a single ensemble forecast.',
  },
  {
    n: '03',
    title: 'Score',
    text: 'Actuals settle each day, and every forecaster is ranked on accuracy.',
  },
  {
    n: '04',
    title: 'Rewards',
    text: 'Each month, the prize pool is split among the most accurate forecasters.',
  },
];

// Rotates slogans with a typewriter effect: type, hold, delete, next.
// Reduced-motion users get the full slogan instantly, swapped every 5s.
function useSloganRotator() {
  const [sloganIdx, setSloganIdx] = useState(0);
  const [typed, setTyped] = useState('');
  const [phase, setPhase] = useState('typing'); // 'typing' | 'holding' | 'deleting'

  useEffect(() => {
    const prefersReduced = typeof window !== 'undefined'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const target = SLOGANS[sloganIdx];

    if (prefersReduced) {
      setTyped(target);
      const t = setTimeout(() => setSloganIdx(i => (i + 1) % SLOGANS.length), 5000);
      return () => clearTimeout(t);
    }

    if (phase === 'typing') {
      if (typed.length < target.length) {
        const t = setTimeout(() => setTyped(target.slice(0, typed.length + 1)), 45);
        return () => clearTimeout(t);
      }
      const t = setTimeout(() => setPhase('holding'), 0);
      return () => clearTimeout(t);
    }
    if (phase === 'holding') {
      const t = setTimeout(() => setPhase('deleting'), 2400);
      return () => clearTimeout(t);
    }
    if (phase === 'deleting') {
      if (typed.length > 0) {
        const t = setTimeout(() => setTyped(typed.slice(0, -1)), 22);
        return () => clearTimeout(t);
      }
      setSloganIdx(i => (i + 1) % SLOGANS.length);
      setPhase('typing');
    }
  }, [typed, phase, sloganIdx]);

  return typed;
}

function useMounted() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, []);
  return mounted;
}

function scrollToSection(id) {
  const el = document.getElementById(id);
  if (!el) return;
  const reduced = typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  el.scrollIntoView({behavior: reduced ? 'auto' : 'smooth', block: 'start'});
}

function Reveal({as: Tag = 'div', className = '', delay = 0, children}) {
  const [ref, inView] = useInView();
  return (
    <Tag
      ref={ref}
      className={`motion-safe:transition-all motion-safe:duration-700 motion-safe:ease-out ${
        inView ? 'opacity-100 translate-y-0' : 'motion-safe:opacity-0 motion-safe:translate-y-3'
      } ${className}`}
      style={{transitionDelay: `${delay}ms`}}
    >
      {children}
    </Tag>
  );
}

// On-mount variant of the §7.2 reveal pattern, with a stagger delay per block.
function HeroStagger({mounted, delay = 0, className = '', children}) {
  return (
    <div
      className={`motion-safe:transition-all motion-safe:duration-700 motion-safe:ease-out ${
        mounted ? 'opacity-100 translate-y-0' : 'motion-safe:opacity-0 motion-safe:translate-y-3'
      } ${className}`}
      style={{transitionDelay: `${delay}ms`}}
    >
      {children}
    </div>
  );
}

function Homepage() {
  const dispatch = useDispatch();
  const markets = useSelector(selectMarketResources);
  const mounted = useMounted();
  const typedSlogan = useSloganRotator();

  useEffect(() => {
    dispatch(fetchMarketResources());
  }, [dispatch]);

  const stats = useMemo(() => {
    const list = markets || [];
    const active = list.filter(m => !!m.is_active);
    const totalBudget = active.reduce((sum, r) => {
      const n = parseFloat(r.budget);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
    const totalForecasters = parseInt(list[0]?.total_unique_forecasters, 10) || 0;
    return {activeCount: active.length, totalBudget, totalForecasters};
  }, [markets]);

  const budgetLabel = stats.totalBudget > 0
    ? `€${stats.totalBudget.toLocaleString(undefined, {maximumFractionDigits: 0})}`
    : '—';

  return (
    <div className="min-h-screen bg-surface dark:bg-gray-870 text-slate-900 dark:text-gray-100 flex flex-col">
      {/* Hero fills the first viewport on every breakpoint; the how-it-works
          section only appears on scroll. */}
      <div className="flex-1 flex flex-col min-h-screen">
      <PublicNav/>

      <main className="flex-1 flex flex-col w-full sm:justify-center">
        {/* Mobile: flex column that fills the fold and distributes its bands evenly.
            sm+: reverts to the original grid (stacked on sm, two-column on lg). */}
        <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-6 py-6 sm:py-16 w-full flex flex-1 flex-col justify-evenly gap-6 sm:flex-none sm:grid sm:grid-cols-1 sm:gap-10 lg:grid-cols-2 lg:items-center">
          <div className="text-center lg:text-left">
            <HeroStagger mounted={mounted}>
              <div className="text-[11px] uppercase tracking-[0.22em] text-brand-600 dark:text-brand-400 font-medium">
                Energy forecasting market
              </div>
            </HeroStagger>

            {/* 48px only on wide displays (2xl+); laptops get 44px.
                -ml cancels the glyph side bearing so the heading optically aligns
                with the tagline and buttons (left-aligned layouts only). */}
            <HeroStagger mounted={mounted} delay={80}>
              <h1 className="mt-4 text-3xl sm:text-4xl lg:text-[2.75rem] 2xl:text-5xl lg:-ml-[3px] font-light leading-[1.05] -tracking-[0.02em] text-slate-900 dark:text-white">
                <span className="block">
                  Collaborative forecasting
                </span>
                <span className="block">
                  made <span className="italic font-semibold text-brand-600 dark:text-brand-400">simple</span>.
                </span>
              </h1>
            </HeroStagger>

            <HeroStagger mounted={mounted} delay={160}>
              <div className="mt-4 sm:mt-6 min-h-[2.5rem] flex items-center justify-center lg:justify-start">
                <p
                  aria-live="polite"
                  aria-atomic="true"
                  className="text-sm sm:text-[0.9375rem] 2xl:text-base text-slate-600 dark:text-gray-300 max-w-xl leading-relaxed"
                >
                  {typedSlogan}
                  <span aria-hidden="true" className="typewriter-caret">|</span>
                </p>
              </div>
            </HeroStagger>

            <HeroStagger mounted={mounted} delay={240}>
              <div className="mt-6 sm:mt-8 flex flex-wrap items-center justify-center lg:justify-start gap-3">
                <PublicButton to="/apply" variant="primary" withArrow className="min-w-[150px]">
                  Apply to join
                </PublicButton>
                <PublicButton to="/markets" variant="secondary" className="min-w-[150px]">
                  Browse markets
                </PublicButton>
              </div>
            </HeroStagger>
          </div>

          <HeroStagger mounted={mounted} delay={200}>
            <div className="rounded-lg overflow-hidden ring-1 ring-slate-900/10 dark:ring-gray-100/10 shadow-lg">
              <div className="relative w-full" style={{paddingTop: '56.25%'}}>
                <iframe
                  title="Predico intro"
                  src="https://www.youtube-nocookie.com/embed/09BYZO0FMvA?autoplay=1&mute=1&loop=1&playlist=09BYZO0FMvA&rel=0&modestbranding=1&iv_load_policy=3&playsinline=1&controls=1&cc_load_policy=0&hl=en&cc_lang_pref=en"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full border-0"
                />
              </div>
            </div>
            <p className="mt-2 text-xs text-slate-500 dark:text-gray-400 text-center">
              See details on our{' '}
              <Link
                to="/about"
                className="underline hover:text-slate-900 dark:hover:text-white transition"
              >
                Proof of Concept with Elia Group
              </Link>
            </p>
          </HeroStagger>

          {/* Mobile: contact + built-by become a distributed band (hidden on sm+) */}
          <div className="sm:hidden w-full flex flex-col items-center gap-4">
            <div className="flex flex-col items-center gap-2 mt-4">
              <div className="text-xs text-slate-500 dark:text-gray-400">
                contact: <a href="mailto:predico@inesctec.pt"
                            className="hover:underline text-slate-700 dark:text-gray-300">predico@inesctec.pt</a>
              </div>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-400 dark:text-gray-500">
                <span>Built by</span>
                <img src={inescLogo} alt="INESC TEC" className="h-4 object-contain opacity-80"/>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Desktop: inline mono stats row at the bottom edge of the hero (sm+ only) */}
      <div className="hidden sm:flex w-full px-6 pb-8 flex-wrap items-center justify-center gap-x-7 gap-y-2 font-mono text-sm text-slate-700 dark:text-gray-300">
        <span className="inline-flex items-center gap-2">
          <span className="inline-flex h-2 w-2 rounded-full bg-emerald-500"/>
          {stats.activeCount} active {stats.activeCount === 1 ? 'market' : 'markets'}
        </span>
        <span className="text-slate-300 dark:text-gray-600">·</span>
        <span>{budgetLabel} monthly prize pool</span>
        <span className="text-slate-300 dark:text-gray-600">·</span>
        <span>{stats.totalForecasters} active {stats.totalForecasters === 1 ? 'forecaster' : 'forecasters'}</span>
        <span className="text-slate-300 dark:text-gray-600">·</span>
        <span>intraday → days-ahead horizons</span>
      </div>

      {/* Scroll cue (sm+); decorative loop, disabled under reduced motion */}
      <div className="hidden sm:flex justify-center pb-8">
        <button
          type="button"
          onClick={() => scrollToSection('how-it-works')}
          aria-label="Scroll to how it works"
          className="p-1 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ring-offset-2 ring-offset-surface dark:ring-offset-gray-870"
        >
          <ChevronDownIcon aria-hidden="true"
                           className="h-5 w-5 text-slate-500 dark:text-gray-400 motion-safe:animate-bounce"/>
        </button>
      </div>
      </div>

      {/* How it works: numbered steps beside the animated ensemble diagram.
          The right column starts at the section top, aligned with the title. */}
      <section id="how-it-works" className="w-full border-t border-slate-900/10 dark:border-gray-100/10 scroll-mt-24">
        <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-6 py-16 sm:py-24">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:items-start">
            <div>
              <Reveal className="max-w-2xl">
                <div className="text-[11px] uppercase tracking-[0.22em] text-brand-600 dark:text-brand-400 font-medium">
                  How it works
                </div>
                <h2 className="mt-4 text-2xl sm:text-3xl font-light leading-[1.1] -tracking-[0.02em] text-slate-900 dark:text-white">
                  Many forecasts in. One{' '}
                  <span className="italic font-semibold text-brand-600 dark:text-brand-400">stronger</span>
                  {' '}forecast out.
                </h2>
                <p className="mt-4 text-base sm:text-lg text-slate-700 dark:text-gray-300 leading-relaxed">
                  Every hour, a new market session opens for live energy forecasting. Forecasters
                  submit probabilistic predictions; the platform blends them into an ensemble and
                  scores everyone against reality.
                </p>
              </Reveal>

              <ol className="mt-12 sm:mt-16 space-y-8">
                {STEPS.map((s, i) => (
                  <Reveal as="li" key={s.n} delay={i * 100}>
                    <div className="flex items-baseline gap-4">
                      <span className="font-mono text-xs text-slate-400 dark:text-gray-500">{s.n}</span>
                      <div>
                        <h3 className="text-lg font-medium text-slate-900 dark:text-white">{s.title}</h3>
                        <p className="mt-1 text-sm leading-relaxed text-slate-600 dark:text-gray-300">{s.text}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </ol>
            </div>

            <Reveal delay={200}>
              {/* Invisible replica of the left column's eyebrow row (same
                  classes) so the diagram top — and with it the "gate closure"
                  axis label — aligns with the h2 title on lg+. */}
              <div aria-hidden="true"
                   className="hidden lg:block invisible text-[11px] uppercase tracking-[0.22em] font-medium">
                How it works
              </div>
              <div className="lg:mt-4">
                <EnsembleDiagram/>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Platform at a glance: markets, forecasters, prize pool, about */}
      <section className="w-full border-t border-slate-900/10 dark:border-gray-100/10">
        <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-6 py-14 sm:py-16">
          <Reveal>
            <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 sm:gap-x-10 sm:gap-y-12 lg:grid-cols-4 lg:gap-0 lg:divide-x lg:divide-slate-900/10 lg:dark:divide-gray-100/10">
              <Link to="/markets" className="group lg:px-10 lg:first:pl-0 lg:last:pr-0">
                <div className="inline-flex items-center gap-2.5 text-4xl font-mono font-light tracking-tight tabular-nums text-slate-900 dark:text-white">
                  <span aria-hidden="true" className="inline-flex h-2 w-2 rounded-full bg-emerald-500"/>
                  {stats.activeCount}
                </div>
                <div className="mt-2 text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-gray-400 font-medium">
                  available {stats.activeCount === 1 ? 'market' : 'markets'}
                </div>
                <div className="mt-3 text-sm text-brand-600 dark:text-brand-400 group-hover:underline underline-offset-4">
                  Browse markets →
                </div>
              </Link>

              <div className="lg:px-10 lg:first:pl-0 lg:last:pr-0">
                <div className="text-4xl font-mono font-light tracking-tight tabular-nums text-slate-900 dark:text-white">
                  {stats.totalForecasters}
                </div>
                <div className="mt-2 text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-gray-400 font-medium">
                  active {stats.totalForecasters === 1 ? 'forecaster' : 'forecasters'}
                </div>
                <div className="mt-3 text-sm text-slate-500 dark:text-gray-400">
                  submitted in the last 30 days
                </div>
              </div>

              <div className="lg:px-10 lg:first:pl-0 lg:last:pr-0">
                <div className="text-4xl font-mono font-light tracking-tight tabular-nums text-slate-900 dark:text-white">
                  {budgetLabel}
                </div>
                <div className="mt-2 text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-gray-400 font-medium">
                  monthly prize pool
                </div>
                <div className="mt-3 text-sm text-slate-500 dark:text-gray-400">
                  split per horizon and track
                </div>
              </div>

              <Link to="/about" className="group lg:px-10 lg:first:pl-0 lg:last:pr-0">
                <div className="inline-flex items-center gap-2 text-4xl font-light tracking-tight text-slate-900 dark:text-white">
                  See more
                  <ArrowRightIcon aria-hidden="true"
                                  className="h-6 w-6 transition-transform group-hover:translate-x-1"/>
                </div>
                <div className="mt-2 text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-gray-400 font-medium">
                  Methodology, results and the Elia proof of concept
                </div>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <PublicFooter/>
    </div>
  );
}

export default Homepage;
